/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
import { CodetableListState } from './list/types-store';
import { CodetableItemState } from './item/types-store';
import { TextCollection } from 'app/types-shared';

export enum CodetableFeature {
  parent = 'parent',
  node = 'node',
  disabled = 'disabled',
  sequence = 'sequence',
  description = 'description',
  longDescription = 'longDescription',
  userGroup = 'userGroup', // -> subset
  visible = 'visible', // -> exclude
  caseType = 'caseType', // -> application
  category = 'category',
  hierarchy = 'hierarchy',
  icon = 'icon',
  icon2 = 'icon2',
  colour = 'colour',
  measurementSystem = 'measurementSystem',
  language = 'language'
}

export type CodetableFeatures = {
  [key in CodetableFeature]?: boolean;
};

export enum CodeFieldWireKey {
  code = 'code',
  node = 'node',
  subset = 'subset',
  exclude = 'exclude',
  application = 'application',
  category = 'category',
  class = 'class',
  colourCode = 'colourCode',
  measurementSystem = 'measurementSystem',
  factor = 'factor',
  minValue = 'minValue',
  maxValue = 'maxValue',
  warningDeviationPercent = 'warningDeviationPercent'
}

export interface CodeFieldWire {
  id: number;
  searchRequired: boolean;
  label?: string;
}

export type CodeFieldsWire = { [key in keyof typeof CodeFieldWireKey]: CodeFieldWire; };

export enum CodeFieldFlagWireKey {
  disabled = 'disabled',
  hierarchy = 'hierarchy',
  sequence = 'sequence',
  level = 'level',
  icon = 'icon',
  decimalColour = 'decimalColour',
  colour = 'colour',
  scoreCalcWeight = 'scoreCalcWeight',
  merge = 'merge'
}

export type CodeFieldFlagsWire = { [key in keyof typeof CodeFieldFlagWireKey]: boolean; };

export type CodetablesAccessWire = AccessCodetables[];
export type CodesAccessWire = AccessCodes[];

export type CodeLanguageField = {
  description: boolean;
  longDescription: boolean;
};

export type CodeFieldIncludeInactive = {
  enabled: boolean;
};

export type CodeCompanyRegisterId = {
  required: boolean;
};

export enum CodeFieldIncludeInactiveKey {
  active = 'active',
  all = 'all'
}

export type CodeFieldUserCode = {
  text: string;
  negated: boolean;
}

export type CodeFieldIncludeInactiveValues = { [key in keyof typeof CodeFieldIncludeInactiveKey]: CodeFieldIncludeInactive; };

export interface CodeMetadataWire extends CodeFieldsWire, CodeFieldFlagsWire {
  formName: CodeFormName;
  label: string;
  minLevel?: number;
  language: CodeLanguageField;
  companyRegisterId?: CodeCompanyRegisterId;
}

export enum CodetableScreenName {
  RiskArea = 'frmAdminC_RiskArea',
  RiskMatrix = 'frmAdminC_Risk_Matrix',
  Consequence = 'frmAdminConsequence',
  CheckListHierarchy = 'frmAdminC_Check_List',
  Company = 'frmAdminC_Company',
  CauseCategory = 'frmAdminC_CauseCategory',
  InteractionOption = 'frmAdminC_InteractionOption',
  Reference = 'frmAdminC_Reference',
  RecurrenceFrequency = 'frmAdminTemplateC_Risk_Level',
  Likelihood = 'frmAdminTemplateC_Risk_Level',

  Severity = 'frmAdminTemplateC_Risk_Level',
  SeverityNode = 'frmAdminTemplateC_Severity_Node',

  ReferenceOption = 'frmAdminC_ReferenceOption',
  IntegrityStatus = 'frmAdminC_IntegrityStatus',
  Evaluation = 'frmAdminC_Evaluation',
  SubstanceHierarchy = 'frmAdminC_Substance',
  SubstanceCategory = 'frmAdminX_Substance_Category',
  UnitOfMeasureSet = 'frmAdminC_Uom_Set',
  UnitOfMeasure = 'frmAdminC_UoM',
  UserGroup = 'frmAdminC_Subset',
  UnitOfMeasureConversionType = 'frmAdminC_Uom_Conversion_Type',
  InsuranceType = 'frmAdminC_Insurance_Type',
  SecurityBreachType = 'frmAdminC_SecurityBreachType',
  GasType = 'frmAdminC_GasType'
}

export type CodeFormName = CodetableScreenName | string;

export interface CodeField {
  id: number;
  searchRequired: boolean;
  label?: string;
}

export interface CodetableMetadata {
  formName: CodeFormName;
  codetable: CodeField;
  codetableClass: CodeField;
  label: string;
  codetableNode: CodeField;
  codetableCategory: CodeField;
  colourCode?: CodeField;
  companyRegisterId?: CodeCompanyRegisterId;
  features: CodetableFeatures;
  merge?: boolean;
}

export interface AccessCodetables {
  id: number;
  create: boolean;
  administrationCentral: boolean;
}

export interface AccessCodes {
  id: number;
  edit: boolean;
  delete: boolean;
  move: boolean;
  readonlySubsets?: number[];
}

export interface CodetableAccess {
  codetables: AccessCodetables;
  codes: AccessCodes;
}

export interface CodetableAccessState {
  fetchingCodetables: number;
  fetchingCodes: number;
  data: CodetableAccess;
}

export interface CodetableMetadataState {
  fetching: number;
  data: CodetableMetadata;
}

export interface CodetableAdministrationState {
  metadata: CodetableMetadataState;
  access: CodetableAccessState;
  list: CodetableListState;
  item: CodetableItemState;
  texts: TextCollection;
}

export interface ConfirmPromptState {
  show: boolean; 
  codetableId: number;
  codeId: number;
  description: string; 
  childrenCount: number;
}
